import { elapsedMs, type GameState } from '@arrow/game-engine';
import { type CSSProperties, useEffect, useState } from 'react';

import { CHALLENGER, type ChallengeContext, paceView } from '../game/challenge.ts';

interface PaceTrackProps {
	game: GameState;
	/** Answers on the board. */
	total: number;
	challenge: ChallengeContext;
	/** Tests: a fixed clock. */
	now?: () => number;
}

/**
 * The pace ghost, under the masthead during a challenge: your answers as a vermilion bar,
 * the challenger’s at the same elapsed time as an ink tick, and one quiet line ("Your
 * challenger had 9 by now. You're ahead."). Anonymous, like every challenge. Counts only, never which answers. It never takes focus
 * or speaks (the clock already does); it re-renders itself once a second while the clock
 * runs, and only moves by width transitions, which reduced motion turns off.
 */
export function PaceTrack({ game, total, challenge, now = Date.now }: PaceTrackProps) {
	const running = game.runningSince !== null;
	const [t, setT] = useState(now);
	useEffect(() => {
		setT(now());
		if (!running) return;
		const id = window.setInterval(() => setT(now()), 1000);
		return () => window.clearInterval(id);
	}, [running, game, now]);
	const view = paceView({
		mine: game.solvedAnswers.length,
		total,
		paceMs: challenge.paceMs,
		elapsedMs: elapsedMs(game, t),
		durationMs: challenge.durationMs,
	});
	const vars = { '--pace-you': view.minePos, '--pace-ghost': view.theirsPos } as CSSProperties;
	return (
		<div className="pace" data-state={view.state} style={vars} data-testid="pace">
			<div className="pace-track" aria-hidden="true">
				<span className="pace-you" />
				<span className="pace-ghost" />
			</div>
			<p className="pace-note">
				<span className="sr-only">
					You have {view.mine} of {view.total} answers.{' '}
				</span>
				<b>{CHALLENGER}</b>
				{view.note.slice(CHALLENGER.length)}
			</p>
		</div>
	);
}
